import { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';

interface PoiType {
  id: string;
  name: string;
  label: string;
  svg?: string;
  color: string;
}

interface AdminPoiTypesProps {
  embedded?: boolean;
}

export default function AdminPoiTypes({ embedded = false }: AdminPoiTypesProps) {
  const [types, setTypes] = useState<PoiType[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', label: '', svg: '', color: '#94a3b8' });
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', label: '', svg: '', color: '#94a3b8' });

  const loadTypes = useCallback(async () => {
    try {
      const res = await api.pois.getTypes();
      setTypes(res);
    } catch (err) {
      console.error('Failed to load POI types:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  const handleEdit = (t: PoiType) => {
    setEditingId(t.id);
    setEditForm({ name: t.name, label: t.label, svg: t.svg || '', color: t.color });
  };

  const handleSave = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      await api.pois.updateType(editingId, {
        name: editForm.name,
        label: editForm.label,
        svg: editForm.svg || undefined,
        color: editForm.color,
      });
      setEditingId(null);
      await loadTypes();
    } catch (err) {
      console.error('Failed to update POI type:', err);
      alert('Failed to update POI type');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t: PoiType) => {
    if (!window.confirm(`Delete POI type "${t.label}"? POIs using this type will be changed to "other".`)) return;
    try {
      await api.pois.deleteType(t.id);
      setTypes((prev) => prev.filter((x) => x.id !== t.id));
    } catch (err) {
      console.error('Failed to delete POI type:', err);
      alert('Failed to delete POI type');
    }
  };

  const handleCreate = async () => {
    if (!createForm.name || !createForm.label) {
      alert('Name (slug) and label are required');
      return;
    }
    setSaving(true);
    try {
      await api.pois.createType({
        name: createForm.name,
        label: createForm.label,
        svg: createForm.svg || undefined,
        color: createForm.color,
      });
      setCreating(false);
      setCreateForm({ name: '', label: '', svg: '', color: '#94a3b8' });
      await loadTypes();
    } catch (err: any) {
      console.error('Failed to create POI type:', err);
      alert(err?.message || 'Failed to create POI type');
    } finally {
      setSaving(false);
    }
  };

  const renderSvgPreview = (svg: string | undefined, color: string) => {
    if (!svg) {
      return (
        <span className="w-8 h-8 rounded-full inline-block border-2 border-white shadow-sm" style={{ backgroundColor: color }} />
      );
    }
    // Inject colour if not specified
    let svgStr = svg;
    if (!svgStr.includes('fill=') && !svgStr.includes('fill:')) {
      svgStr = svgStr.replace('<svg', `<svg fill="${color}"`);
    }
    return (
      <span
        className="inline-flex w-8 h-8 items-center justify-center"
        dangerouslySetInnerHTML={{ __html: svgStr }}
      />
    );
  };

  return (
    <div className={embedded ? 'space-y-6' : 'p-6 space-y-6'}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`${embedded ? 'text-xl' : 'text-2xl'} font-bold text-slate-900 dark:text-white`}>POI Types</h1>
          {!embedded && (
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Manage the types of points of interest and their map icons.
            </p>
          )}
        </div>
        <button
          onClick={() => setCreating(true)}
          className="btn btn-primary"
        >
          + Add Type
        </button>
      </div>

      {/* Create form */}
      {creating && (
        <div className="card p-4 space-y-3">
          <h2 className="font-semibold text-slate-900 dark:text-white">Create New POI Type</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                Name (slug) *
              </label>
              <input
                type="text"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm"
                placeholder="e.g. boat_lift"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                Label *
              </label>
              <input
                type="text"
                value={createForm.label}
                onChange={(e) => setCreateForm({ ...createForm, label: e.target.value })}
                className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm"
                placeholder="e.g. Boat Lift"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                Colour
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={createForm.color}
                  onChange={(e) => setCreateForm({ ...createForm, color: e.target.value })}
                  className="w-8 h-8 rounded cursor-pointer border-0"
                />
                <input
                  type="text"
                  value={createForm.color}
                  onChange={(e) => setCreateForm({ ...createForm, color: e.target.value })}
                  className="flex-1 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm font-mono"
                />
              </div>
            </div>
            <div className="flex items-end">
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500 dark:text-slate-400">Preview:</span>
                {renderSvgPreview(createForm.svg, createForm.color)}
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
              SVG Icon (paste full SVG markup)
            </label>
            <textarea
              value={createForm.svg}
              onChange={(e) => setCreateForm({ ...createForm, svg: e.target.value })}
              rows={4}
              className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm font-mono"
              placeholder='<svg viewBox="0 0 24 24">...</svg>'
            />
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={saving} className="btn btn-primary text-sm disabled:opacity-50">
              {saving ? 'Creating...' : 'Create Type'}
            </button>
            <button onClick={() => setCreating(false)} className="btn text-sm">Cancel</button>
          </div>
        </div>
      )}

      {/* Types list */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-32 mb-2" />
              <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-20" />
            </div>
          ))}
        </div>
      ) : types.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-slate-500 dark:text-slate-400">No POI types defined</p>
        </div>
      ) : (
        <div className="space-y-3">
          {types.map((t) => (
            <div key={t.id} className="card">
              {editingId === t.id ? (
                <div className="p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Name (slug)</label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                        className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Label</label>
                      <input
                        type="text"
                        value={editForm.label}
                        onChange={(e) => setEditForm({ ...editForm, label: e.target.value })}
                        className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Colour</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={editForm.color}
                          onChange={(e) => setEditForm({ ...editForm, color: e.target.value })}
                          className="w-8 h-8 rounded cursor-pointer border-0"
                        />
                        <input
                          type="text"
                          value={editForm.color}
                          onChange={(e) => setEditForm({ ...editForm, color: e.target.value })}
                          className="flex-1 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm font-mono"
                        />
                      </div>
                    </div>
                    <div className="flex items-end">
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-500 dark:text-slate-400">Preview:</span>
                        {renderSvgPreview(editForm.svg, editForm.color)}
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">SVG Icon</label>
                    <textarea
                      value={editForm.svg}
                      onChange={(e) => setEditForm({ ...editForm, svg: e.target.value })}
                      rows={4}
                      className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm font-mono"
                      placeholder='<svg viewBox="0 0 24 24">...</svg>'
                    />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={handleSave} disabled={saving} className="btn btn-primary text-sm disabled:opacity-50">
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => setEditingId(null)} className="btn text-sm">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {renderSvgPreview(t.svg, t.color)}
                    <div>
                      <div className="font-medium text-slate-900 dark:text-white">{t.label}</div>
                      <div className="text-xs text-slate-400 font-mono">{t.name}</div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <span className="w-4 h-4 rounded-full border border-slate-200 dark:border-slate-600" style={{ backgroundColor: t.color }} />
                      <span className="text-xs text-slate-500 dark:text-slate-400 font-mono">{t.color}</span>
                    </div>
                    {t.svg && (
                      <span className="text-xs px-2 py-0.5 rounded bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400">
                        Custom SVG
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => handleEdit(t)} className="px-3 py-1.5 text-xs rounded bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-600 dark:text-slate-300">
                      Edit
                    </button>
                    <button onClick={() => handleDelete(t)} className="px-3 py-1.5 text-xs rounded bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400">
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
